import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Calendar, AlertTriangle, Clock, Save, X } from 'lucide-react';

const FermeturesTemporaires = ({ 
  commercantId, 
  fermetures = [], 
  onUpdate, 
  loading = false 
}) => {
  const [editMode, setEditMode] = useState(false);
  const [localFermetures, setLocalFermetures] = useState(fermetures);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setLocalFermetures(fermetures);
  }, [fermetures]);

  const validateFermeture = (fermeture) => {
    const errors = {};
    
    if (!fermeture.dateDebut) {
      errors.dateDebut = 'La date de début est requise';
    }
    
    if (!fermeture.dateFin) {
      errors.dateFin = 'La date de fin est requise';
    }
    
    if (fermeture.dateDebut && fermeture.dateFin) {
      const debut = new Date(fermeture.dateDebut);
      const fin = new Date(fermeture.dateFin);
      
      if (fin < debut) {
        errors.dateFin = 'La date de fin doit être postérieure à la date de début';
      }
      
      // Vérifier si la fermeture est dans le passé
      const aujourdhui = new Date();
      aujourdhui.setHours(0, 0, 0, 0);
      
      if (fin < aujourdhui) {
        errors.dateFin = 'La fermeture ne peut pas se terminer dans le passé';
      }
    }
    
    return errors;
  };

  const addFermeture = () => {
    const nouvelleFermeture = {
      id: `temp_${Date.now()}`,
      dateDebut: '',
      dateFin: '',
      raison: '',
      messageClient: '',
      masquerCatalogue: false,
      autoriserPrecommande: false,
      autoriserReservationTable: false,
      actif: true
    };
    
    setLocalFermetures([...localFermetures, nouvelleFermeture]);
  };

  const updateFermeture = (id, field, value) => {
    const updated = localFermetures.map(f => 
      f.id === id ? { ...f, [field]: value } : f
    );
    setLocalFermetures(updated);
    
    // Valider cette fermeture spécifique
    const fermeture = updated.find(f => f.id === id);
    const fermetureErrors = validateFermeture(fermeture);
    
    setErrors(prev => ({
      ...prev,
      [id]: fermetureErrors
    }));
  };

  const removeFermeture = (id) => {
    setLocalFermetures(localFermetures.filter(f => f.id !== id));
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  };

  const validateAll = () => {
    const allErrors = {};
    let hasErrors = false;
    
    localFermetures.forEach(fermeture => {
      const fermetureErrors = validateFermeture(fermeture);
      if (Object.keys(fermetureErrors).length > 0) {
        allErrors[fermeture.id] = fermetureErrors;
        hasErrors = true;
      }
    });
    
    setErrors(allErrors);
    return !hasErrors;
  };

  const handleSave = async () => {
    if (!validateAll()) {
      return;
    }

    try {
      await onUpdate(localFermetures);
      setEditMode(false);
      setErrors({});
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setErrors({ general: 'Erreur lors de la sauvegarde des fermetures' });
    }
  };

  const handleCancel = () => {
    setLocalFermetures(fermetures);
    setEditMode(false);
    setErrors({});
  };

  const getFermetureStatus = (fermeture) => {
    const aujourdhui = new Date();
    const debut = new Date(fermeture.dateDebut);
    const fin = new Date(fermeture.dateFin);
    
    if (aujourdhui >= debut && aujourdhui <= fin) {
      return { status: 'active', color: 'red', text: 'Actuellement fermé' };
    } else if (aujourdhui < debut) {
      return { status: 'future', color: 'yellow', text: 'Fermeture programmée' };
    } else {
      return { status: 'past', color: 'gray', text: 'Fermeture passée' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <div className="bg-white border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Calendar className="h-5 w-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-900">Fermetures temporaires</h3>
        </div>
        {!editMode && (
          <button
            onClick={() => setEditMode(true)}
            className="px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 rounded-md hover:bg-orange-100 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Gérer</span>
          </button>
        )}
      </div>

      {/* Erreur générale */}
      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center space-x-2">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-700">{errors.general}</span>
        </div>
      )}

      {editMode ? (
        <div className="space-y-4">
          {/* Liste des fermetures */}
          {localFermetures.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Aucune fermeture temporaire configurée</p>
              <p className="text-sm">Ajoutez des périodes de fermeture exceptionnelle</p>
            </div>
          ) : (
            localFermetures.map((fermeture) => {
              const fermetureErrors = errors[fermeture.id] || {};
              return (
                <div key={fermeture.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        Fermeture {fermeture.id.startsWith('temp_') ? '(nouvelle)' : ''}
                      </span>
                    </div>
                    <button
                      onClick={() => removeFermeture(fermeture.id)}
                      className="text-red-600 hover:text-red-700 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Date de début */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de début
                      </label>
                      <input
                        type="date"
                        value={fermeture.dateDebut}
                        onChange={(e) => updateFermeture(fermeture.id, 'dateDebut', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                          fermetureErrors.dateDebut ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {fermetureErrors.dateDebut && (
                        <p className="text-red-600 text-xs mt-1">{fermetureErrors.dateDebut}</p>
                      )}
                    </div>

                    {/* Date de fin */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de fin
                      </label>
                      <input
                        type="date"
                        value={fermeture.dateFin}
                        onChange={(e) => updateFermeture(fermeture.id, 'dateFin', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                          fermetureErrors.dateFin ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {fermetureErrors.dateFin && (
                        <p className="text-red-600 text-xs mt-1">{fermetureErrors.dateFin}</p>
                      )}
                    </div>

                    {/* Raison */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Raison (optionnel)
                      </label>
                      <input
                        type="text"
                        value={fermeture.raison}
                        onChange={(e) => updateFermeture(fermeture.id, 'raison', e.target.value)}
                        placeholder="Ex: Vacances, Formation, Maintenance..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    {/* Message client */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message pour les clients (optionnel)
                      </label>
                      <textarea
                        value={fermeture.messageClient}
                        onChange={(e) => updateFermeture(fermeture.id, 'messageClient', e.target.value)}
                        placeholder="Ex: Nous sommes fermés pour les vacances d'été. Réouverture le 15 août."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <div className="md:col-span-2 space-y-3 pt-2 border-t border-gray-100">
                      <p className="text-sm font-medium text-gray-700">Comportement côté client</p>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(fermeture.masquerCatalogue)}
                          onChange={(e) => updateFermeture(fermeture.id, 'masquerCatalogue', e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">
                          <strong>Griser le catalogue</strong> — les produits restent visibles avec la date de réouverture sur chaque photo
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(fermeture.autoriserPrecommande)}
                          onChange={(e) => updateFermeture(fermeture.id, 'autoriserPrecommande', e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">
                          <strong>Autoriser les précommandes</strong> — commandes possibles pour une date de retrait après la fermeture
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(fermeture.autoriserReservationTable)}
                          onChange={(e) => updateFermeture(fermeture.id, 'autoriserReservationTable', e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">
                          <strong>Autoriser les réservations de table</strong> — uniquement pour des dates après la fermeture
                        </span>
                      </label>
                      <p className="text-xs text-gray-500">
                        La caisse POS n&apos;est jamais bloquée par une fermeture temporaire.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {/* Bouton ajouter fermeture */}
          <div className="flex justify-center">
            <button
              onClick={addFermeture}
              className="px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 rounded-md hover:bg-orange-100 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter une fermeture</span>
            </button>
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center space-x-2"
            >
              <X className="h-4 w-4" />
              <span>Annuler</span>
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            </button>
          </div>
        </div>
      ) : (
        /* Mode lecture */
        <div className="space-y-4">
          {localFermetures.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Aucune fermeture temporaire configurée</p>
            </div>
          ) : (
            localFermetures.map((fermeture) => {
              const status = getFermetureStatus(fermeture);
              return (
                <div key={fermeture.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {formatDate(fermeture.dateDebut)} - {formatDate(fermeture.dateFin)}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          status.color === 'red' ? 'bg-red-100 text-red-800' :
                          status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {status.text}
                        </span>
                      </div>
                      
                      {fermeture.raison && (
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Raison :</strong> {fermeture.raison}
                        </p>
                      )}
                      
                      {fermeture.messageClient && (
                        <p className="text-sm text-gray-600">
                          <strong>Message client :</strong> {fermeture.messageClient}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-2 mt-2">
                        {fermeture.masquerCatalogue && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                            Catalogue grisé
                          </span>
                        )}
                        {fermeture.autoriserPrecommande && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                            Précommandes
                          </span>
                        )}
                        {fermeture.autoriserReservationTable && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                            Résa table après fermeture
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-800">Informations</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Pendant une fermeture, les clients ne peuvent pas commander pour cette période (sauf options cochées).
                  Vous pouvez griser le catalogue, autoriser les précommandes ou les réservations de table après la réouverture.
                  La caisse POS reste toujours disponible.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FermeturesTemporaires;






